import { useQuery } from "@tanstack/react-query";

import { supabase } from "@/integrations/supabase/client";
import { toOrgSocialLinks, type OrgSocialLink } from "@/lib/orgSocialHandles";
import { cn } from "@/lib/utils";
import { SOCIAL_PLATFORM_COLORS, SocialPlatformIcon } from "./SocialPlatformIcon";

/**
 * The org's saved social accounts, read through the curated public RPC
 * (`get_org_social_connections_public` — active rows, no token columns).
 * Independent of the hidden Social & Community posts feed: this is just the
 * list of places to follow the org.
 */
export const orgSocialLinksQueryKey = (orgId: string | undefined) => ["org-social-links", orgId] as const;

/** The one read of an org's social links; the hook and the pre-publish Trust Review share it. */
export async function fetchOrgSocialLinks(orgId: string): Promise<OrgSocialLink[]> {
  const { data, error } = await supabase.rpc("get_org_social_connections_public", { p_org_id: orgId });
  if (error) throw error;
  return toOrgSocialLinks(data);
}

export function useOrgSocialLinks(orgId: string | undefined) {
  return useQuery({
    queryKey: orgSocialLinksQueryKey(orgId),
    queryFn: () => fetchOrgSocialLinks(orgId!),
    enabled: !!orgId,
    staleTime: 60_000,
  });
}

interface OrgSocialLinksProps {
  links: OrgSocialLink[] | undefined;
  className?: string;
}

/**
 * Icon links to the org's social accounts. Renders nothing at all when the org
 * has none — an unselected profile item must be absent, not greyed out.
 */
export function OrgSocialLinks({ links, className }: OrgSocialLinksProps) {
  if (!links?.length) return null;

  return (
    <ul className={cn("flex flex-wrap items-center gap-2", className)} aria-label="Social accounts">
      {links.map((link) => {
        const colors = SOCIAL_PLATFORM_COLORS[link.platform];
        return (
          <li key={link.platform}>
            <a
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              title={`${link.label}: ${link.handle}`}
              aria-label={`${link.label}: ${link.handle} (opens in a new tab)`}
              className={cn(
                "inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-card transition-colors hover:border-primary/40",
                colors.bg,
              )}
            >
              <SocialPlatformIcon platform={link.platform} className={cn("h-4 w-4", colors.text)} />
            </a>
          </li>
        );
      })}
    </ul>
  );
}
